import React, { useState, useEffect, useCallback } from 'react';
import { dashboardService, DashboardResumen, DashboardNegocio, DashboardEmpresa } from '../src/services/dashboardService';
import { useAuth } from '../src/contexts/AuthContext';

interface DashboardProps {
  onClose: () => void;
}

type Periodo = 'dia' | 'semana' | 'mes' | 'anio' | 'todo';
type ViewTab = 'general' | 'negocios' | 'empresas';

const PERIODOS: { key: Periodo; label: string; icon: string }[] = [
  { key: 'dia',    label: 'Hoy',     icon: 'today' },
  { key: 'semana', label: 'Semana',   icon: 'date_range' },
  { key: 'mes',    label: 'Mes',      icon: 'calendar_month' },
  { key: 'anio',   label: 'Año',      icon: 'calendar_today' },
  { key: 'todo',   label: 'Todo',     icon: 'all_inclusive' },
];

const CHART_LABEL: Record<Periodo, string> = {
  dia:    'Ventas por Hora',
  semana: 'Ventas por Día',
  mes:    'Ventas por Semana',
  anio:   'Ventas por Mes',
  todo:   'Ventas por Mes',
};

const formatLabel = (fecha: string, periodo: Periodo): string => {
  if (periodo === 'dia') {
    return fecha.slice(11, 16) || fecha;
  }
  if (periodo === 'semana') {
    const parts = fecha.split('-');
    if (parts.length >= 3) {
      const dias = ['Dom','Lun','Mar','Mie','Jue','Vie','Sab'];
      const d = new Date(fecha);
      return dias[d.getDay()] || `${parts[2]}/${parts[1]}`;
    }
    return fecha;
  }
  if (periodo === 'mes') {
    // Formato: YYYY-W## (ej: 2026-W16)
    const match = fecha.match(/W(\d+)/);
    if (match) {
      return `Sem ${parseInt(match[1], 10)}`;
    }
    return fecha;
  }
  if (periodo === 'anio' || periodo === 'todo') {
    const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
    const parts = fecha.split('-');
    if (parts.length >= 2) {
      const m = parseInt(parts[1], 10);
      return meses[m - 1] || parts[1];
    }
    return fecha;
  }
  return fecha;
};

const Dashboard: React.FC<DashboardProps> = ({ onClose }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.nombreRol === 'SuperAdmin';

  const [data, setData] = useState<DashboardResumen | null>(null);

  // Ocultar scroll del body cuando el Dashboard está abierto
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [periodo, setPeriodo] = useState<Periodo>('mes');
  const [tab, setTab] = useState<ViewTab>('general');
  const [selectedEmpresa, setSelectedEmpresa] = useState<DashboardEmpresa | null>(null);
  const [selectedNegocio, setSelectedNegocio] = useState<DashboardNegocio | null>(null);
  const [chartMode, setChartMode] = useState<'ventas' | 'pedidos'>('ventas');

  const loadData = useCallback(async (p: Periodo) => {
    try {
      setLoading(true);
      setError(null);
      const empresaId = isSuperAdmin ? undefined : user?.empresaId;
      const resumen = await dashboardService.getResumen(empresaId, p);
      setData(resumen);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar dashboard');
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, user?.empresaId]);

  useEffect(() => {
    loadData(periodo);
  }, [periodo, loadData]);

  const handlePeriodoChange = (p: Periodo) => {
    setPeriodo(p);
    setSelectedEmpresa(null);
    setSelectedNegocio(null);
  };

  const formatMoney = (n: number) => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatCompact = (n: number) => {
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
  };

  const maxChartVal = data?.ventasDiarias.length
    ? Math.max(...data.ventasDiarias.map(d => chartMode === 'ventas' ? d.totalVentas : d.totalPedidos), 1)
    : 1;

  const estatusData = data
    ? [
        { label: 'Pendiente',  value: data.pedidosPendientes, color: 'bg-yellow-500', ring: 'ring-yellow-500' },
        { label: 'Preparando', value: data.pedidosPreparando, color: 'bg-blue-500',   ring: 'ring-blue-500' },
        { label: 'En camino',  value: data.pedidosEnCamino,   color: 'bg-orange-500', ring: 'ring-orange-500' },
        { label: 'Entregado',  value: data.pedidosEntregados, color: 'bg-green-500',  ring: 'ring-green-500' },
        { label: 'Cancelado',  value: data.pedidosCancelados, color: 'bg-red-500',    ring: 'ring-red-500' },
      ]
    : [];

  const totalEstatus = estatusData.reduce((s, e) => s + e.value, 0) || 1;

  // ─── Render helpers ───

  const renderStatCard = (icon: string, label: string, value: string, accent: string) => (
    <div className="bg-white dark:bg-white/5 rounded-2xl p-5 flex items-center gap-4 shadow-sm border border-gray-100 dark:border-white/5">
      <div className={`w-12 h-12 rounded-xl ${accent} flex items-center justify-center shrink-0`}>
        <span className="material-symbols-outlined text-white text-xl">{icon}</span>
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">{label}</p>
        <p className=" text-gray-900 dark:text-white truncate">{value}</p>
      </div>
    </div>
  );

  const renderEstatusBar = (items: { label: string; value: number; color: string }[], total: number) => (
    <div>
      <div className="flex rounded-full overflow-hidden h-4 bg-gray-200 dark:bg-white/10">
        {items.filter(i => i.value > 0).map((item, idx) => (
          <div
            key={idx}
            className={`${item.color} transition-all duration-500`}
            style={{ width: `${(item.value / total) * 100}%` }}
            title={`${item.label}: ${item.value}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${item.color}`} />
            <span className="text-sm text-gray-500 dark:text-gray-400">{item.label}</span>
            <span className="text-sm font-black text-gray-700 dark:text-white">{item.value}</span>
            <span className="text-sm text-gray-400">({((item.value / total) * 100).toFixed(0)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderChart = () => {
    if (!data || data.ventasDiarias.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">bar_chart</span>
          <p className="text-gray-400 font-bold text-sm">Sin datos para este período</p>
        </div>
      );
    }

    return (
      <div>
        {/* Chart toggle */}
        <div className="flex items-center justify-between mb-4">
          <p className=" font-black text-gray-400 uppercase">{CHART_LABEL[periodo]}</p>
          <div className="flex gap-1 bg-gray-100 dark:bg-white/5 rounded-lg p-0.5">
            <button
              onClick={() => setChartMode('ventas')}
              className={`px-3 py-1 rounded-md  font-bold transition-colors ${
                chartMode === 'ventas' ? 'bg-primary text-white' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >Ventas $</button>
            <button
              onClick={() => setChartMode('pedidos')}
              className={`px-3 py-1 rounded-md  font-bold transition-colors ${
                chartMode === 'pedidos' ? 'bg-primary text-white' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >Pedidos #</button>
          </div>
        </div>

        {/* Y-axis labels + bars */}
        <div className="flex gap-2">
          <div className="flex flex-col justify-between text-right pr-1 py-1 shrink-0" style={{ height: '220px' }}>
            <span className="text-[9px] text-gray-400">{chartMode === 'ventas' ? formatCompact(maxChartVal) : maxChartVal}</span>
            <span className="text-[9px] text-gray-400">{chartMode === 'ventas' ? formatCompact(maxChartVal / 2) : Math.round(maxChartVal / 2)}</span>
            <span className="text-[9px] text-gray-400">0</span>
          </div>
          <div className="flex-1 overflow-x-auto">
            <div className="flex items-end gap-[3px]" style={{ height: '220px', minWidth: `${data.ventasDiarias.length * 24}px` }}>
              {data.ventasDiarias.map((d, i) => {
                const val = chartMode === 'ventas' ? d.totalVentas : d.totalPedidos;
                const h = (val / maxChartVal) * 100;
                return (
                  <div key={i} className="flex flex-col items-center flex-1 min-w-[20px] group relative h-full justify-end">
                    <div className="absolute bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-[10px] px-3 py-2 rounded-xl whitespace-nowrap pointer-events-none z-10 shadow-lg">
                      <p className="font-bold">{d.fecha}</p>
                      <p>{formatMoney(d.totalVentas)}</p>
                      <p>{d.totalPedidos} pedidos</p>
                    </div>
                    <div
                      className="w-full bg-primary/70 hover:bg-primary rounded-t-md transition-all duration-300 cursor-pointer"
                      style={{ height: `${Math.max(h, 1.5)}%` }}
                    />
                  </div>
                );
              })}
            </div>
            {/* X-axis labels */}
            <div className="flex gap-[3px] mt-1" style={{ minWidth: `${data.ventasDiarias.length * 24}px` }}>
              {data.ventasDiarias.map((d, i) => {
                const showLabel = data.ventasDiarias.length <= 12
                  || i === 0
                  || i === data.ventasDiarias.length - 1
                  || i % Math.ceil(data.ventasDiarias.length / 8) === 0;
                return (
                  <div key={i} className="flex-1 min-w-[20px] text-center">
                    <span className="text-[10px] text-gray-400">
                      {showLabel ? formatLabel(d.fecha, periodo) : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderNegocioCard = (neg: DashboardNegocio) => {
    const negEstatus = [
      { label: 'Pendiente', value: neg.pendientes, color: 'bg-yellow-500' },
      { label: 'Preparando', value: neg.preparando, color: 'bg-blue-500' },
      { label: 'En camino', value: neg.enCamino, color: 'bg-orange-500' },
      { label: 'Entregado', value: neg.entregados, color: 'bg-green-500' },
      { label: 'Cancelado', value: neg.cancelados, color: 'bg-red-500' },
    ];
    const negTotal = neg.totalPedidos || 1;

    return (
      <button
        key={neg.negocioId}
        onClick={() => setSelectedNegocio(selectedNegocio?.negocioId === neg.negocioId ? null : neg)}
        className={`text-left w-full bg-white dark:bg-white/5 rounded-2xl p-5 hover:shadow-md transition-all border-2 ${
          selectedNegocio?.negocioId === neg.negocioId ? 'border-primary shadow-md' : 'border-gray-100 dark:border-white/5'
        }`}
      >
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <p className="text-sm font-black text-gray-900 dark:text-white truncate">{neg.nombreNegocio}</p>
            <p className="text-[11px] text-gray-400 truncate">{neg.nombreEmpresa}</p>
          </div>
          <span className="text-sm font-black text-primary shrink-0">{formatMoney(neg.totalVentas)}</span>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <p className="text-[10px] text-gray-400 uppercase">Pedidos</p>
            <p className="text-sm font-black text-gray-800 dark:text-white">{neg.totalPedidos}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase">Promedio</p>
            <p className="text-sm font-black text-gray-800 dark:text-white">{formatMoney(neg.promedioTicket)}</p>
          </div>
        </div>
        {renderEstatusBar(negEstatus, negTotal)}
      </button>
    );
  };

  const renderEmpresaCard = (emp: DashboardEmpresa) => {
    const empEstatus = [
      { label: 'Pendiente', value: emp.pendientes, color: 'bg-yellow-500' },
      { label: 'Preparando', value: emp.preparando, color: 'bg-blue-500' },
      { label: 'En camino', value: emp.enCamino, color: 'bg-orange-500' },
      { label: 'Entregado', value: emp.entregados, color: 'bg-green-500' },
      { label: 'Cancelado', value: emp.cancelados, color: 'bg-red-500' },
    ];
    const empTotal = emp.totalPedidos || 1;

    return (
      <button
        key={emp.empresaId}
        onClick={() => setSelectedEmpresa(selectedEmpresa?.empresaId === emp.empresaId ? null : emp)}
        className={`text-left w-full bg-white dark:bg-white/5 rounded-2xl p-5 hover:shadow-md transition-all border-2 ${
          selectedEmpresa?.empresaId === emp.empresaId ? 'border-primary shadow-md' : 'border-gray-100 dark:border-white/5'
        }`}
      >
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <p className="text-sm font-black text-gray-900 dark:text-white truncate">{emp.nombreEmpresa}</p>
            <p className="text-[11px] text-gray-400">{emp.totalNegocios} negocio(s)</p>
          </div>
          <span className="text-sm font-black text-primary shrink-0">{formatMoney(emp.totalVentas)}</span>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <p className="text-[10px] text-gray-400 uppercase">Pedidos</p>
            <p className="text-sm font-black text-gray-800 dark:text-white">{emp.totalPedidos}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase">Promedio</p>
            <p className="text-sm font-black text-gray-800 dark:text-white">{formatMoney(emp.promedioTicket)}</p>
          </div>
        </div>
        {renderEstatusBar(empEstatus, empTotal)}
      </button>
    );
  };

  // ─── Full-screen Layout ───

  return (
    <div className="pt-4 fixed inset-0 z-50 bg-gray-50 dark:bg-[#121010] flex flex-col overflow-hidden">

      {/* ── Top bar ── */}
      <div className="bg-white dark:bg-[#1a1614] border-b border-gray-100 dark:border-white/10 shrink-0">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <span className="material-symbols-outlined text-primary text-lg">dashboard</span>
              </div>
              <div>
                <h1 className="text-lg  text-gray-900 dark:text-white leading-tight">Dashboard</h1>
                <p className="font-bold text-[13px] text-gray-400 leading-tight">
                  {isSuperAdmin ? 'Todas las empresas' : 'Mi empresa'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Period selector */}
              <div className="hidden sm:flex items-center gap-1 bg-gray-100 dark:bg-white/5 rounded-xl p-1">
                {PERIODOS.map(p => (
                  <button
                    key={p.key}
                    onClick={() => handlePeriodoChange(p.key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg  font-bold transition-all ${
                      periodo === p.key
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-white/10'
                    }`}
                  >
                    <span className="material-symbols-outlined ">{p.icon}</span>
                    {p.label}
                  </button>
                ))}
              </div>

              <button
                onClick={() => loadData(periodo)}
                disabled={loading}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 transition-colors disabled:opacity-50"
                title="Recargar"
              >
                <span className={`material-symbols-outlined text-gray-400 ${loading ? 'animate-spin' : ''}`}>refresh</span>
              </button>
              <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                <span className="material-symbols-outlined text-gray-400">close</span>
              </button>
            </div>
          </div>

          {/* Mobile period selector */}
          <div className="sm:hidden flex gap-1 pb-3 overflow-x-auto">
            {PERIODOS.map(p => (
              <button
                key={p.key}
                onClick={() => handlePeriodoChange(p.key)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
                  periodo === p.key
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400'
                }`}
              >
                <span className="material-symbols-outlined text-sm">{p.icon}</span>
                {p.label}
              </button>
            ))}
          </div>

          {/* View tabs */}
          <div className="flex gap-1 pb-3 overflow-x-auto">
            {(['general', 'negocios', ...(isSuperAdmin ? ['empresas'] : [])] as ViewTab[]).map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 py-2 rounded-xl  font-bold whitespace-nowrap transition-colors ${
                  tab === t
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                    : 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10'
                }`}
              >
                {t === 'general' && 'Resumen General'}
                {t === 'negocios' && 'Por Negocio'}
                {t === 'empresas' && 'Por Empresa'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin w-10 h-10 border-[3px] border-primary border-t-transparent rounded-full"></div>
                <p className="text-sm text-gray-400 font-bold">Cargando datos...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-32 text-center">
              <span className="material-symbols-outlined text-5xl text-red-400 mb-3">error</span>
              <p className="text-red-500 font-bold text-lg">{error}</p>
              <button onClick={() => loadData(periodo)} className="mt-4 px-5 py-2.5 bg-primary text-white rounded-xl text-sm font-bold hover:opacity-90 transition-opacity">
                Reintentar
              </button>
            </div>
          ) : data ? (
            <>
              {/* ── General tab ── */}
              {tab === 'general' && (
                <div className="space-y-6">
                  {/* KPI Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {renderStatCard('receipt_long', 'Pedidos', data.totalPedidos.toLocaleString(), 'bg-primary')}
                    {renderStatCard('payments', 'Ventas', formatMoney(data.totalVentas), 'bg-green-500')}
                    {renderStatCard('avg_pace', 'Ticket Prom.', formatMoney(data.promedioTicket), 'bg-blue-500')}
                    {renderStatCard('local_shipping', 'Envíos', formatMoney(data.totalEnvios), 'bg-orange-500')}
                    {renderStatCard('toll', 'Tarifa Serv.', formatMoney(data.totalTarifaServicio), 'bg-purple-500')}
                  </div>

                  {/* Charts row */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Main chart */}
                    <div className="lg:col-span-2 bg-white dark:bg-white/5 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-white/5">
                      {renderChart()}
                    </div>

                    {/* Status donut-like panel */}
                    <div className="bg-white dark:bg-white/5 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-white/5">
                      <p className=" font-black text-gray-400 uppercase mb-4">Distribución por Estatus</p>
                      {/* Circular stats */}
                      <div className="grid grid-cols-2 gap-3 mb-5">
                        {estatusData.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full ${item.color}/20 flex items-center justify-center shrink-0`}>
                              <span className={`text-sm font-black ${item.color.replace('bg-', 'text-')}`}>{item.value}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-bold text-gray-600 dark:text-gray-300 truncate">{item.label}</p>
                              <p className="text-xs text-gray-400">{((item.value / totalEstatus) * 100).toFixed(0)}%</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      {renderEstatusBar(estatusData, totalEstatus)}
                    </div>
                  </div>

                  {/* Top negocios */}
                  {data.porNegocio.length > 0 && (
                    <div className="bg-white dark:bg-white/5 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-white/5">
                      <div className="flex items-center justify-between mb-4">
                        <p className=" font-black text-gray-400 uppercase">Top Negocios</p>
                        <button onClick={() => setTab('negocios')} className=" text-primary font-bold hover:underline">
                          Ver todos
                        </button>
                      </div>
                      <div className="space-y-2">
                        {data.porNegocio.slice(0, 8).map((neg, idx) => {
                          const maxNeg = data.porNegocio[0]?.totalVentas || 1;
                          const barW = (neg.totalVentas / maxNeg) * 100;
                          return (
                            <div key={neg.negocioId} className="flex items-center gap-3">
                              <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                <span className=" font-black text-primary">{idx + 1}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <p className=" font-bold text-gray-900 dark:text-white truncate">{neg.nombreNegocio}</p>
                                  <span className=" font-black text-primary shrink-0 ml-2">{formatMoney(neg.totalVentas)}</span>
                                </div>
                                <div className="h-1.5 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
                                  <div className="h-full bg-primary/60 rounded-full transition-all duration-500" style={{ width: `${barW}%` }} />
                                </div>
                                <p className=" text-gray-400 mt-0.5">{neg.totalPedidos} pedidos · {neg.nombreEmpresa}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Negocios tab ── */}
              {tab === 'negocios' && (
                <div className="space-y-4">
                  {data.porNegocio.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-32 text-center">
                      <span className="material-symbols-outlined text-5xl text-gray-300 mb-3">store</span>
                      <p className="text-gray-400 font-bold">No hay datos de negocios en este período</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {data.porNegocio.map(renderNegocioCard)}
                    </div>
                  )}
                </div>
              )}

              {/* ── Empresas tab ── */}
              {tab === 'empresas' && isSuperAdmin && (
                <div className="space-y-4">
                  {data.porEmpresa.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-32 text-center">
                      <span className="material-symbols-outlined text-5xl text-gray-300 mb-3">apartment</span>
                      <p className="text-gray-400 font-bold">No hay datos de empresas en este período</p>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {data.porEmpresa.map(renderEmpresaCard)}
                      </div>

                      {selectedEmpresa && (
                        <div className="mt-6">
                          <div className="flex items-center gap-2 mb-4">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                              <span className="material-symbols-outlined text-primary text-sm">domain</span>
                            </div>
                            <p className="text-sm font-black text-gray-900 dark:text-white">
                              Negocios de {selectedEmpresa.nombreEmpresa}
                            </p>
                          </div>
                          {data.porNegocio.filter(n => n.empresaId === selectedEmpresa.empresaId).length === 0 ? (
                            <p className="text-center text-gray-400 text-sm py-8">Sin negocios con pedidos en este período</p>
                          ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                              {data.porNegocio
                                .filter(n => n.empresaId === selectedEmpresa.empresaId)
                                .map(renderNegocioCard)}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
